import { RECONCILIATION_LABELS } from '@/modules/projects/components/ReconciliationBadge'
import type { Side } from '@/modules/projects/components/Workbench/FromTo/types'
import { sentenceCase } from '@/modules/projects/components/Workbench/moves'
import type {
  EditAccountFacts,
  EditPreview,
  EditReading,
  EditStatementFacts,
  EditStatementImpact,
  ReconciliationStatus,
} from '@/modules/projects/stores/types'
import { numberToCurrency } from '@/utils'

const verdictOf = (reading: EditReading) =>
  RECONCILIATION_LABELS[reading.status.toUpperCase() as ReconciliationStatus] ??
  reading.status

// The file line under the dates, so a long file name never pushes the dates out of view.
export const fileLine = (subject: EditStatementFacts) =>
  [
    subject.fileName,
    subject.pages,
    `${subject.transactions.toLocaleString()} transactions`,
  ]
    .filter(Boolean)
    .join(' · ')

const accountSide = (facts: EditAccountFacts | null, missing: string): Side =>
  facts
    ? {
        headline: [facts.type && sentenceCase(facts.type), facts.number]
          .filter(Boolean)
          .join(' '),
        lines: [facts.institution ?? 'Bank not known', facts.owner].filter(
          (line): line is string => Boolean(line),
        ),
      }
    : { headline: missing, lines: [] }

interface SidePair {
  label: string | null
  from: Side
  to: Side
}

const bare = (headline: string): Side => ({ headline, lines: [] })

// An account edit has its two accounts. A re-cut has a pair per statement whose pages change. Any
// other edit shows its one change, such as the dates.
export const pairsOf = (preview: EditPreview): SidePair[] => {
  if (preview.sides) {
    return [
      {
        label: null,
        from: accountSide(preview.sides.from, 'Not in the project yet'),
        to: accountSide(preview.sides.to, 'Removed from the project'),
      },
    ]
  }
  if (preview.kind === 'recut_file') {
    return preview.changes.map((change) => ({
      label: sentenceCase(change.label),
      from: bare(change.from ?? 'Not a statement yet'),
      to: bare(change.to ?? 'No longer a statement'),
    }))
  }
  const [change] = preview.changes
  return [
    {
      label: null,
      from: bare(change?.from ?? 'Nothing'),
      to: bare(change?.to ?? 'Nothing'),
    },
  ]
}

// One statement: say what its verdict and gap do, including when they hold, because "nothing
// breaks" is the answer the user is looking for.
const oneStatementLines = ({ before, after }: EditStatementImpact) => {
  const lines: string[] = []
  if (before.status !== after.status)
    lines.push(`Status goes from ${verdictOf(before)} to ${verdictOf(after)}.`)
  if (before.gap !== after.gap)
    lines.push(
      `Gap goes from ${numberToCurrency(before.gap)} to ${numberToCurrency(after.gap)}.`,
    )
  if (lines.length === 0)
    lines.push(
      `Status (${verdictOf(before)}) and gap (${numberToCurrency(before.gap)}) stay the same.`,
    )
  return lines
}

// Many statements: count them by the verdict they move between, not one line each.
const manyStatementLines = (statements: EditStatementImpact[]) => {
  const tally = new Map<string, number>()
  for (const { before, after } of statements) {
    if (before.status === after.status) continue
    const move = `${verdictOf(before)} to ${verdictOf(after)}`
    tally.set(move, (tally.get(move) ?? 0) + 1)
  }
  const lines = [...tally].map(
    ([move, count]) =>
      `${count} ${count === 1 ? 'statement goes' : 'statements go'} from ${move}.`,
  )
  const unbalanced = statements.filter(
    ({ before, after }) => before.gap === 0 && after.gap !== 0,
  ).length
  if (unbalanced > 0)
    lines.push(
      `${unbalanced} ${unbalanced === 1 ? 'statement no longer balances' : 'statements no longer balance'}.`,
    )
  return lines
}

export const whatChanges = (preview: EditPreview, showsVerdict: boolean) => {
  const fields =
    preview.kind === 'edit_account'
      ? preview.changes.map(
          (change) =>
            `${change.label} goes from ${change.from ?? 'none'} to ${change.to ?? 'none'}.`,
        )
      : []
  const verdicts = !showsVerdict
    ? []
    : preview.statements.length === 1
      ? oneStatementLines(preview.statements[0])
      : manyStatementLines(preview.statements)
  return [...fields, ...preview.consequences, ...verdicts]
}
